import React, { memo, useCallback, useEffect } from 'react';
import { Send, Play } from 'lucide-react';
import { ConversationStep } from '../../types/suggestionWizard';
import { INPUT_PLACEHOLDERS, WIZARD_TEXT, TEXTAREA_CONFIG } from '../../constants/suggestionWizard';

interface WizardInputAreaProps {
  currentStep: ConversationStep;
  inputValue: string;
  selectedSignalsCount: number;
  isRunningSignal?: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onRunSignals: () => void;
  onClearChips?: () => void;
}

/**
 * Input area component for the wizard panel.
 * Shows textarea or run signals button based on current step.
 */
export const WizardInputArea = memo<WizardInputAreaProps>(({
  currentStep,
  inputValue,
  selectedSignalsCount,
  isRunningSignal = false,
  inputRef,
  onInputChange,
  onSubmit,
  onKeyPress,
  onRunSignals,
  onClearChips,
}) => {
  // Auto-resize textarea
  const adjustTextareaHeight = useCallback((textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(
        Math.max(textarea.scrollHeight, TEXTAREA_CONFIG.MIN_HEIGHT),
        TEXTAREA_CONFIG.MAX_HEIGHT
      );
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInputChange(e.target.value);
    adjustTextareaHeight(e.target);
    // Clear chip selections if user manually edits
    if (onClearChips) {
      onClearChips();
    }
  }, [onInputChange, adjustTextareaHeight, onClearChips]);

  // Adjust height when inputValue changes programmatically (e.g., pre-filled text)
  useEffect(() => {
    adjustTextareaHeight(inputRef.current);
  }, [inputValue, adjustTextareaHeight, inputRef]);

  const placeholder = INPUT_PLACEHOLDERS[currentStep] || INPUT_PLACEHOLDERS.default;

  return (
    <div
      className="p-4 border-t border-gray-100 bg-white flex-shrink-0"
      style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
    >
      {currentStep === 'show_signals' ? (
        <button
          onClick={onRunSignals}
          disabled={selectedSignalsCount === 0 || isRunningSignal}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[14px] font-medium transition-all ${
            isRunningSignal
              ? 'bg-gray-900 text-white opacity-75 cursor-wait'
              : selectedSignalsCount > 0
                ? 'bg-gray-900 text-white hover:bg-gray-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isRunningSignal ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Preparing preview...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" fill={selectedSignalsCount > 0 ? 'currentColor' : 'none'} />
              {selectedSignalsCount > 0
                ? WIZARD_TEXT.RUN_SIGNALS(selectedSignalsCount)
                : WIZARD_TEXT.SELECT_SIGNALS_TO_RUN
              }
            </>
          )}
        </button>
      ) : (
        <div className="relative bg-gray-50 border border-gray-200 rounded-2xl focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-sm transition-all">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={onKeyPress}
            placeholder={placeholder}
            disabled={currentStep === 'generating'}
            className="w-full px-4 py-3 pr-12 text-[14px] bg-transparent rounded-2xl outline-none resize-none placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minHeight: `${TEXTAREA_CONFIG.MIN_HEIGHT}px`, maxHeight: `${TEXTAREA_CONFIG.MAX_HEIGHT}px`, lineHeight: '1.5' }}
          />
          <button
            onClick={onSubmit}
            disabled={currentStep === 'generating'}
            className="absolute right-2 bottom-2 w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center transition-all hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
});

WizardInputArea.displayName = 'WizardInputArea';

export default WizardInputArea;
